import {
  getActionCapability,
  type ActionCapability,
  type ActionDomain,
} from '@cogo/action-registry'

export type ActionFlowLane = 'action' | 'ui' | 'motion'

export interface ActionExecutionModel {
  actionName: string
  lane: ActionFlowLane
  atomic: boolean
  completion: 'immediate' | 'accepted' | 'completed'
  directBindingAllowed: boolean
  capability?: ActionCapability
}

export interface ActionFlowExecutionSummary {
  actionCount: number
  lanes: ActionFlowLane[]
  stageCount: number
  sagaCount: number
  completionBarrierCount: number
}

const LANE_ORDER: ActionFlowLane[] = ['action', 'ui', 'motion']

export function actionDomainToLane(domain: ActionDomain): ActionFlowLane {
  if (domain === 'motion') return 'motion'
  if (['navigation', 'external-link', 'event', 'ui'].includes(domain)) return 'ui'
  return 'action'
}

/**
 * Studio projection of the canonical action capability contract.
 * A direct event binding is offered only for a public, active, web-grounded,
 * generation-allowed atomic action. Everything else belongs in a named flow.
 */
export function buildActionExecutionModel(actionName: string): ActionExecutionModel {
  const capability = getActionCapability(actionName)
  const semantics = capability?.semantics
  return {
    actionName,
    lane: semantics ? actionDomainToLane(semantics.domain) : 'action',
    atomic: semantics?.atomic ?? false,
    completion: semantics?.completion ?? 'completed',
    directBindingAllowed: Boolean(
      capability &&
        capability.semantics.atomic &&
        capability.generationPolicy === 'allow' &&
        capability.runtimeImplemented &&
        capability.targetSupport.web === true &&
        capability.registryExposure === 'public' &&
        capability.lifecycle === 'active',
    ),
    capability,
  }
}

export function summarizeActionFlowExecution(steps: readonly unknown[]): ActionFlowExecutionSummary {
  const lanes = new Set<ActionFlowLane>()
  let actionCount = 0
  let stageCount = 0
  let sagaCount = 0
  let completionBarrierCount = 0

  const visit = (items: readonly unknown[]) => {
    for (const candidate of items) {
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue
      const step = candidate as Record<string, unknown>
      const type = typeof step.type === 'string' ? step.type : undefined
      if (type === 'stage') stageCount += 1
      if (type === 'saga') sagaCount += 1

      const actionName = typeof step.action === 'string' ? step.action : undefined
      if (actionName) {
        const execution = buildActionExecutionModel(actionName)
        actionCount += 1
        lanes.add(execution.lane)
        if (execution.completion === 'completed') completionBarrierCount += 1
      }

      for (const field of [
        'then',
        'else',
        'steps',
        'try',
        'catch',
        'finally',
        'default',
        'onEnter',
        'onSuccess',
        'onError',
        'onFinally',
        'compensate',
        'stages',
      ]) {
        const branch = step[field]
        if (Array.isArray(branch)) visit(branch)
      }

      if (step.cases && typeof step.cases === 'object' && !Array.isArray(step.cases)) {
        for (const branch of Object.values(step.cases as Record<string, unknown>)) {
          if (Array.isArray(branch)) visit(branch)
        }
      }
    }
  }

  visit(steps)
  return {
    actionCount,
    lanes: LANE_ORDER.filter((lane) => lanes.has(lane)),
    stageCount,
    sagaCount,
    completionBarrierCount,
  }
}
